import { FC, useMemo } from "react";
import { Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next"; // Import useTranslation

import { GetTasterExperimentSummaryResponse } from "api/client";

import { useWindowSize } from "hooks/window-size";

import { Tag } from "components/common/Tag";
import { Tooltip } from "components/common/Tooltip";

import {
  StyledCardInfo,
  StyledExperimentCard,
  StyledExperimentCardHeader,
  StyledCardItem,
} from "styles/features/home";

interface ProjectCardProps {
  experiment: GetTasterExperimentSummaryResponse;
  withStatuses?: boolean;

  onClick?: () => void;
}

const ExperimentCard: FC<ProjectCardProps> = ({ experiment, onClick }) => {
  const theme = useTheme();
  const { t } = useTranslation(); // Initialize translation

  const { windowSize } = useWindowSize();

  const { name, isApproved, schedule } = experiment;

  const { startTime, location } = schedule;

  const renderedStatus = useMemo(
    () =>
      isApproved ? (
        <Tag status="approved" label="" />
      ) : (
        <Tag status="need-approval" label="" />
      ),
    [isApproved]
  );

  const renderedDate = useMemo(() => {
    if (startTime) {
      const date = new Date(startTime as string).toLocaleDateString();

      return date !== "Invalid Date" ? date : "-";
    }

    return "-";
  }, [startTime]);

  const renderedTime = useMemo(() => {
    if (startTime) {
      const time = new Intl.DateTimeFormat(undefined, {
        hour: "numeric",
        minute: "numeric",
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }).format(new Date(startTime as string));

      return time !== "Invalid Date" ? time : "-";
    }

    return "-";
  }, [startTime]);

  const charLimitByWindowSize = useMemo(
    () => (windowSize.width >= 1024 ? 30 : 10),
    [windowSize.width]
  );

  return (
    <StyledExperimentCard onClick={onClick}>
      <StyledExperimentCardHeader>
        <Tooltip
          text={name.length > 10 ? name : undefined}
          size="auto"
          wrapClassName="card-tooltip"
          position="bottom-left"
        >
          <Typography
            variant="bodyL"
            fontWeight={500}
            color={theme.palette.common.black}
            maxWidth="100%"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {name}
          </Typography>
        </Tooltip>
        {renderedStatus}
      </StyledExperimentCardHeader>
      <StyledCardInfo>
        <StyledCardItem>
          <Typography variant="bodyS" color="#d3dcea">
            {t('location')} 
          </Typography>
          <Tooltip
            text={
              location && location.length > charLimitByWindowSize
                ? location
                : undefined
            }
            size="auto"
            wrapClassName="card-tooltip"
            position="top-left"
          >
            <Typography
              variant="bodyL"
              color={theme.palette.common.black}
              fontWeight={300}
              className="experiment-value"
            >
              {location || "-"}
            </Typography>
          </Tooltip>
        </StyledCardItem>
        <StyledCardItem>
          <Typography variant="bodyS" color="#d3dcea">
            {t('date')} 
          </Typography>
          <Typography
            variant="bodyL"
            color={theme.palette.common.black}
            fontWeight={300}
            className="experiment-value"
          >
            {renderedDate || "-"}
          </Typography>
        </StyledCardItem>
        <StyledCardItem>
          <Typography variant="bodyS" color="#d3dcea">
            {t('time')} 
          </Typography>
          <Typography
            variant="bodyL"
            color={theme.palette.common.black}
            fontWeight={300}
            className="experiment-value"
          >
            {renderedTime || "-"}
          </Typography>
        </StyledCardItem>
      </StyledCardInfo>
    </StyledExperimentCard>
  );
};

export default ExperimentCard;
